import React, { createContext, useContext, useState, useEffect } from "react";

// Lag context
const CartContext = createContext();

// Hjelp: Hent cart fra localStorage (eller tom hvis ikke finnes)
function getInitialCart() {
  const stored = localStorage.getItem("cart");
  return stored ? JSON.parse(stored) : [];
}

export function CartProvider({ children }) {
  const [cart, setCart] = useState(getInitialCart());

  const totalPrice = cart.reduce(
    (sum, item) => sum + Number(item.price || 0) * (item.quantity || 1),
    0
  );

  // Oppdater localStorage hver gang cart endres
  useEffect(() => {
    localStorage.setItem("cart", JSON.stringify(cart));
  }, [cart]);

  // Normaliser addToCart så den støtter begge kallestiler:
  // 1) addToCart(product, { date_from, date_to, quantity, price })
  // 2) addToCart({ id, name, price, date_from, date_to, quantity, ... })
  function addToCart(arg1, arg2) {
    let product = arg1 || {};
    let options = arg2 || {};

    const looksLikeOneArgPayload =
      arg2 === undefined &&
      product &&
      typeof product === "object" &&
      (
        "price" in product ||
        "date_from" in product ||
        "date_to" in product ||
        "quantity" in product
      );

    if (looksLikeOneArgPayload) {
      options = {
        date_from: product.date_from,
        date_to: product.date_to,
        quantity: product.quantity,
        price: product.price,
      };
    }

    const date_from = options.date_from ?? null;
    const date_to   = options.date_to   ?? null;
    const quantity  = Number(options.quantity ?? 1);
    const price     = Number(
      options.price ??
      product.price ?? // tillat at pris ligger på "produktet"
      0
    );

    // Sjekk om samme produkt for samme periode finnes
    const existingIndex = cart.findIndex(
      (item) =>
        item.id === product.id &&
        item.date_from === date_from &&
        item.date_to === date_to
    );

    if (existingIndex !== -1) {
      const updatedCart = [...cart];
      updatedCart[existingIndex].quantity += quantity;
      // Sikre at prisen ikke blir undefined om første linje manglet
      if (updatedCart[existingIndex].price == null) {
        updatedCart[existingIndex].price = price;
      }
      setCart(updatedCart);
    } else {
      // Behold evt. ekstra felter (image_url osv.)
      const newItem = {
        ...product,
        id: product.id,          // eksplisitt id/navn
        name: product.name,
        price,
        quantity,
        date_from,
        date_to,
      };
      setCart([...cart, newItem]);
    }
  }

  // Fjern produkt (matcher på id + periode for leie)
  function removeFromCart(productId, date_from, date_to) {
    setCart(
      cart.filter(
        (item) =>
          !(
            item.id === productId &&
            item.date_from === date_from &&
            item.date_to === date_to
          )
      )
    );
  }

  // Tøm kurven
  function clearCart() {
    setCart([]);
  }

  // Antall varer (summer quantity)
  const totalItems = cart.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <CartContext.Provider
      value={{ cart, addToCart, removeFromCart, clearCart, totalPrice, totalItems }}
    >
      {children}
    </CartContext.Provider>
  );
}

// Custom hook – for enkel bruk i komponenter
export function useCart() {
  return useContext(CartContext);
}
